/**
 * Utilities for condition type color display.
 * Used when showing condition type codes with a background color (e.g. in protocol step action edit).
 */

/**
 * Returns a contrasting text color (white or black) for a given background hex color.
 * Use white text when background is black or dark so text remains readable.
 * @param {string} hex - CSS hex color (e.g. '#000000', '#ff0000') or empty string
 * @returns {string} '#ffffff' for dark backgrounds, '#000000' for light, or 'inherit' when no color
 */
export function getContrastColor(hex) {
  if (!hex || typeof hex !== 'string' || !hex.startsWith('#')) return 'inherit';
  const h = hex.slice(1);
  if (h.length !== 6 && h.length !== 3) return 'inherit';
  const r = h.length === 6 ? parseInt(h.slice(0, 2), 16) : parseInt(h[0] + h[0], 16);
  const g = h.length === 6 ? parseInt(h.slice(2, 4), 16) : parseInt(h[1] + h[1], 16);
  const b = h.length === 6 ? parseInt(h.slice(4, 6), 16) : parseInt(h[2] + h[2], 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.4 ? '#ffffff' : '#000000';
}

/**
 * Inline style object for displaying a condition type code with optional background color.
 * @param {string} color - Optional hex background color
 * @returns {object} Style object for a span (backgroundColor, color, padding, etc.)
 */
export function getConditionTypeCodeStyle(color) {
  if (!color || typeof color !== 'string' || !color.trim()) {
    return {};
  }
  const hex = color.trim();
  if (!hex.startsWith('#')) return {};
  return {
    backgroundColor: hex,
    color: getContrastColor(hex),
    padding: '2px 6px',
    borderRadius: '4px',
    fontWeight: 500
  };
}
